import { SessionConnectionError } from "@elevenlabs/react";
import type { BaseSessionConfig } from "@elevenlabs/react";
import {
  buildHistoryContext,
  ChatMessage,
  readChat,
  RESUME_FIRST_MESSAGE,
  saveChat,
} from "./chatPersistence";

type SessionOverrides = NonNullable<BaseSessionConfig["overrides"]>;

/** The session ended (End pressed, unmount) before it connected; not an error to show. */
export class SessionEndedError extends Error {}

export function botDynamicVariables(botDetails?: {
  override_first_message?: boolean;
  dynamic_variables?: Record<string, string | number | boolean>;
}) {
  const variables = botDetails?.dynamic_variables;
  if (!variables || botDetails?.override_first_message !== false) return variables;
  return { ...variables, first_message: "" };
}

function buildSessionOverrides(opts: {
  textOnly?: boolean;
  voiceId?: string;
  dynamicVariables?: Record<string, unknown>;
  /** wins over dynamicVariables.first_message (e.g. the greeting on resume) */
  firstMessage?: string;
}): SessionOverrides | undefined {
  const firstMessage =
    opts.firstMessage ??
    (typeof opts.dynamicVariables?.first_message === "string"
      ? (opts.dynamicVariables.first_message as string)
      : undefined);

  if (!opts.textOnly && !opts.voiceId && !firstMessage) return undefined;

  return {
    ...(opts.textOnly && { conversation: { textOnly: true } }),
    ...(opts.voiceId && { tts: { voiceId: opts.voiceId } }),
    ...(firstMessage && { agent: { firstMessage } }),
  };
}

/**
 * @elevenlabs/react 1.x `startSession` returns nothing and reports a failed start to
 * onError. Restores the contract the callers are written for: resolves with the
 * conversation id once connected, rejects with the start error.
 */
function startSessionAsync(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startSession: (options: any) => void,
) {
  return (options: Record<string, unknown>) =>
    new Promise<string>((resolve, reject) => {
      startSession({
        ...options,
        onConnect: ({ conversationId }: { conversationId: string }) =>
          resolve(conversationId),
        onError: (message: string, error?: unknown) =>
          reject(error ?? new Error(message)),
        // Ended while connecting: the provider then drops onConnect/onError, so settle here.
        // Deferred a tick so a real start error (reported right after this status) wins.
        onStatusChange: (status: { status: string }) => {
          (options.onStatusChange as ((s: unknown) => void) | undefined)?.(status);
          if (status.status === "disconnected") {
            setTimeout(
              () => reject(new SessionEndedError("Session ended before connecting")),
              0,
            );
          }
        },
      });
    });
}

/** Close code 3000: the persistent-session token expired or is invalid, or persistence is off for our org. */
const isPersistenceRefused = (err: unknown) =>
  err instanceof SessionConnectionError && err.closeCode === 3000;

// ponytail: page-lifetime flag; persistence is an org-level ElevenLabs flag, so one refusal covers every chat
let persistenceOff = false;

/**
 * `persistentSession` for a websocket text chat (experimental, @elevenlabs/client >= 1.28):
 * resume the stored conversation, or start one that survives a reload for up to 3h.
 */
function persistentSessionFor(token?: string) {
  if (persistenceOff) return undefined;
  return token ? { token } : (true as const);
}

/**
 * After a failed persistent start: true when retrying makes sense. A refused token is
 * dropped, so the retry starts a new conversation; a refused fresh start turns
 * persistence off for the page, so the retry is a plain session.
 */
function retryAfterPersistenceRefused(
  err: unknown,
  token: string | undefined,
  dropToken: () => void,
) {
  if (!isPersistenceRefused(err) || persistenceOff) return false;
  if (token) dropToken();
  else persistenceOff = true;
  return true;
}

/**
 * ElevenLabs rejects `overrides.agent.firstMessage` unless that specific override is
 * enabled in the agent's Security settings — if it isn't, startSession fails/closes.
 * Retry once without the agent override (keeping voice/text-only overrides) rather
 * than leaving the visitor with no conversation at all.
 */
async function startSessionWithOverrideFallback(
  startSession: (options: Record<string, unknown>) => Promise<string>,
  baseParams: Record<string, unknown>,
  overrides: SessionOverrides | undefined,
): Promise<string> {
  try {
    return await startSession({ ...baseParams, ...(overrides && { overrides }) });
  } catch (err) {
    if (
      !overrides?.agent ||
      isPersistenceRefused(err) ||
      err instanceof SessionEndedError
    ) {
      throw err;
    }
    const { agent: _omit, ...rest } = overrides;
    const fallbackOverrides = Object.keys(rest).length > 0 ? rest : undefined;
    return await startSession({
      ...baseParams,
      ...(fallbackOverrides && { overrides: fallbackOverrides }),
    });
  }
}

/**
 * Starts a website chat session; shared by realtime-conversation-bar.tsx and form-bypass-conversation.
 * A websocket text-only chat resumes its stored ElevenLabs conversation; anything else with
 * history starts a new conversation and gets the transcript replayed.
 * Resolves with the conversation id; rejects with SessionEndedError if ended while connecting.
 */
export async function startChatSession(
  conversation: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    startSession: (options: any) => void;
    sendContextualUpdate: (text: string) => void;
  },
  opts: {
    agentId: string;
    /** webrtc voice call; otherwise a websocket chat */
    voice: boolean;
    textOnly: boolean;
    chatKey?: string;
    history: ChatMessage[];
    voiceId?: string;
    dynamicVariables?: Record<string, unknown>;
    onStatusChange: (status: unknown) => void;
  },
): Promise<string> {
  // read now: in skip-intro mode the record is written after the component mounts
  const chat = opts.chatKey ? readChat(opts.chatKey) : null;
  // without a chatKey there is nowhere to keep the persistent-session token
  const persistable = opts.textOnly && !opts.voice && Boolean(opts.chatKey);
  const token = persistable ? chat?.sessionToken : undefined;
  const persistentSession = persistable ? persistentSessionFor(token) : undefined;
  // a resumed conversation already has its history; a new one gets it replayed
  const replay =
    opts.history.length > 0 && typeof persistentSession !== "object";
  try {
    const conversationId = await startSessionWithOverrideFallback(
      startSessionAsync(conversation.startSession),
      {
        agentId: opts.agentId,
        connectionType: opts.voice ? "webrtc" : "websocket",
        textOnly: opts.textOnly,
        ...(opts.dynamicVariables && { dynamicVariables: opts.dynamicVariables }),
        ...(chat?.chatId && { userId: chat.chatId }),
        ...(persistentSession && { persistentSession }),
        onStatusChange: opts.onStatusChange,
      },
      buildSessionOverrides({
        textOnly: opts.textOnly,
        voiceId: opts.voiceId,
        dynamicVariables: opts.dynamicVariables,
        ...(replay && { firstMessage: RESUME_FIRST_MESSAGE }),
      }),
    );
    // Sends only work once connected (after the greeting), hence the static RESUME_FIRST_MESSAGE.
    if (replay) {
      conversation.sendContextualUpdate(buildHistoryContext(opts.history));
    }
    return conversationId;
  } catch (err) {
    if (
      persistentSession &&
      retryAfterPersistenceRefused(err, token, () =>
        saveChat(opts.chatKey!, { sessionToken: undefined }),
      )
    ) {
      return startChatSession(conversation, opts);
    }
    throw err;
  }
}

/** onConversationMetadata handler: keeps the text chat's latest persistent-session token. */
export const rememberSessionToken =
  (chatKey?: string) =>
  ({ persistent_session_token }: { persistent_session_token?: string }) => {
    if (chatKey && persistent_session_token) {
      saveChat(chatKey, { sessionToken: persistent_session_token });
    }
  };
